'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { usePathname } from 'next/navigation';
import Script from 'next/script';

import {
  APP_GTM_ID,
  startAppMeasurement,
  stopAppMeasurement,
  trackAppPage,
} from '~/lib/marketing/app-measurement';
import {
  ANALYTICS_PREFERENCES_EVENT,
  MARKETING_GTM_ID,
  getAnalyticsConsent,
  setAnalyticsConsent,
  startMarketingMeasurement,
  stopMarketingMeasurement,
  subscribeAnalyticsConsent,
  trackMarketingCta,
  trackMarketingPage,
} from '~/lib/marketing/measurement';

import styles from './marketing-analytics.module.css';

const serverConsent = () => 'unknown' as const;

export function MarketingAnalytics({
  production,
  audience = 'website',
}: {
  production: boolean;
  audience?: 'website' | 'app';
}) {
  const pathname = usePathname();
  const consent = useSyncExternalStore(
    subscribeAnalyticsConsent,
    getAnalyticsConsent,
    serverConsent,
  );
  const [ready, setReady] = useState(false);
  const [preferences, setPreferences] = useState(false);
  const [loadScript, setLoadScript] = useState(false);
  const panel = useRef<HTMLElement>(null);
  const preferenceTrigger = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setReady(true);
    const click = (event: MouseEvent) => {
      if (audience === 'app') return;
      const target =
        event.target instanceof Element
          ? event.target.closest('a[href]')
          : null;
      if (
        !(target instanceof HTMLAnchorElement) ||
        target.origin !== location.origin
      )
        return;
      const cta = (
        {
          '/contact': 'contact',
          '/auth/sign-up': 'beta',
          '/pricing': 'pricing',
          '/axcelerate': 'axcelerate',
        } as const
      )[target.pathname as '/contact'];
      if (cta) trackMarketingCta(cta);
    };
    document.addEventListener('click', click);
    const open = (event: Event) => {
      if (!production) return;
      event.preventDefault();
      preferenceTrigger.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      setPreferences(true);
    };
    window.addEventListener(ANALYTICS_PREFERENCES_EVENT, open);
    return () => {
      window.removeEventListener(ANALYTICS_PREFERENCES_EVENT, open);
      document.removeEventListener('click', click);
    };
  }, [audience, production]);

  useEffect(() => {
    if (preferences) panel.current?.focus();
  }, [preferences]);

  useEffect(() => {
    const start =
      audience === 'app' ? startAppMeasurement : startMarketingMeasurement;
    const stop =
      audience === 'app' ? stopAppMeasurement : stopMarketingMeasurement;
    if (consent === 'accepted' && start(production)) {
      setLoadScript(true);
      if (audience === 'app') trackAppPage();
      else trackMarketingPage();
    } else {
      stop();
    }
    return stop;
  }, [consent, pathname, production, audience]);

  function choose(value: 'accepted' | 'rejected') {
    setAnalyticsConsent(value);
    setPreferences(false);
    if (preferences) preferenceTrigger.current?.focus();
  }

  return (
    <>
      {loadScript ? (
        <Script
          id={`leagen-${audience}-gtm`}
          src={`https://www.googletagmanager.com/gtm.js?id=${audience === 'app' ? APP_GTM_ID : MARKETING_GTM_ID}`}
          strategy="afterInteractive"
        />
      ) : null}
      {production && ready && (consent === 'unknown' || preferences) ? (
        <AnalyticsChoicePanel panelRef={panel} onChoose={choose} />
      ) : null}
    </>
  );
}

export function AnalyticsPreferencesButton() {
  return (
    <a
      href="/cookie-policy#controls"
      onClick={(event) => {
        const navigate = window.dispatchEvent(
          new Event(ANALYTICS_PREFERENCES_EVENT, { cancelable: true }),
        );
        if (!navigate) event.preventDefault();
      }}
    >
      Cookie preferences
    </a>
  );
}

export function AnalyticsChoicePanel({
  panelRef,
  onChoose,
  preview = false,
}: {
  panelRef?: React.Ref<HTMLElement>;
  onChoose?: (choice: 'accepted' | 'rejected') => void;
  preview?: boolean;
}) {
  return (
    <section
      ref={panelRef}
      tabIndex={-1}
      className={`${styles.panel} ${preview ? styles.preview : ''}`}
      aria-label="Optional analytics"
    >
      <div>
        <h2>Optional analytics</h2>
        <p>
          Allow Google Analytics and Vercel performance measurement to help us
          understand visits and improve Leagen. Google Analytics uses cookies.
          You can use Leagen either way.{' '}
          <a href="/privacy-policy">Privacy policy</a>
        </p>
      </div>
      <div className={styles.actions}>
        <button type="button" onClick={() => onChoose?.('rejected')}>
          Decline optional analytics
        </button>
        <button
          type="button"
          className={styles.accept}
          onClick={() => onChoose?.('accepted')}
        >
          Allow optional analytics
        </button>
      </div>
    </section>
  );
}
